import { useState } from 'react'
import { 
  Search, 
  Bell, 
  Menu, 
  Sun, 
  Moon, 
  Settings, 
  User, 
  LogOut,
  ChevronDown
} from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Avatar } from '@/components/ui/Avatar'
import { useSidebarStore } from '@/stores/useSidebarStore'
import { useTheme } from '@/app/providers/ThemeProvider'
import { cn } from '@/utils/cn'

export default function Topbar() {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false)
  const { toggle, isOpen } = useSidebarStore()
  const { theme, toggleTheme } = useTheme()

  const notifications = [
    {
      id: '1',
      title: 'New order received',
      message: 'Order #ORD-1024 from John Doe',
      time: '2 min ago',
      unread: true,
    },
    {
      id: '2',
      title: 'Low stock alert',
      message: 'Premium Cotton T-Shirt is running low',
      time: '1 hour ago',
      unread: true,
    },
    {
      id: '3',
      title: 'Payment received',
      message: '$125.50 payment confirmed',
      time: '3 hours ago',
      unread: false,
    },
  ]

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center gap-4 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 px-6">
      {/* Mobile menu button */}
      <Button
        variant="ghost"
        size="sm"
        className="lg:hidden"
        onClick={toggle}
      >
        <Menu className="h-5 w-5" />
      </Button>

      {/* Desktop sidebar toggle */}
      <Button
        variant="ghost"
        size="sm"
        className="hidden lg:flex"
        onClick={toggle}
      >
        <Menu className="h-5 w-5" />
      </Button>

      {/* Search */}
      <div className="flex-1 max-w-md">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search orders, customers, products..."
            className="pl-10"
          />
        </div>
      </div>

  {/* Right side actions */}
  <div className="flex items-center gap-2 ml-auto">
        {/* Theme toggle */}
        <Button
          variant="ghost"
          size="sm"
          onClick={toggleTheme}
          title={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
        >
          {theme === 'light' ? (
            <Moon className="h-5 w-5" />
          ) : (
            <Sun className="h-5 w-5" />
          )}
        </Button>

        {/* Notifications */}
        <div className="relative">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
            className="relative"
          >
            <Bell className="h-5 w-5" />
            {notifications.some(n => n.unread) && (
              <span className="absolute -top-1 -right-1 h-2 w-2 bg-destructive rounded-full" />
            )}
          </Button>

          {/* Notifications dropdown */}
          {isNotificationsOpen && (
            <div className="absolute right-0 top-full mt-2 w-80 bg-popover border rounded-xl shadow-lg z-50">
              <div className="p-4 border-b">
                <h3 className="font-semibold">Notifications</h3>
              </div>
              <div className="max-h-96 overflow-y-auto">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={cn(
                      'p-4 border-b last:border-b-0 hover:bg-accent cursor-pointer',
                      notification.unread && 'bg-muted/50'
                    )}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <p className="font-medium text-sm">{notification.title}</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          {notification.message}
                        </p>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {notification.time}
                      </span>
                    </div>
                    {notification.unread && (
                      <div className="w-2 h-2 bg-primary rounded-full mt-2" />
                    )}
                  </div>
                ))}
              </div>
              <div className="p-4 border-t">
                <Button variant="ghost" size="sm" className="w-full">
                  View all notifications
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* User menu */}
        <div className="relative">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
            className="flex items-center gap-2"
          >
            <Avatar size="sm" fallback="Admin User" />
            <span className="hidden md:inline text-sm">Admin User</span>
            <ChevronDown className="h-4 w-4" />
          </Button>

          {/* User dropdown */}
          {isUserMenuOpen && (
            <div className="absolute right-0 top-full mt-2 w-56 bg-popover border rounded-xl shadow-lg z-50">
              <div className="p-4 border-b">
                <p className="font-medium">Admin User</p>
                <p className="text-sm text-muted-foreground">admin@thredz.com</p>
              </div>
              <div className="p-2">
                <Button variant="ghost" size="sm" className="w-full justify-start">
                  <User className="h-4 w-4 mr-2" />
                  Profile
                </Button>
                <Button variant="ghost" size="sm" className="w-full justify-start">
                  <Settings className="h-4 w-4 mr-2" />
                  Settings
                </Button>
                <div className="border-t my-2" />
                <Button variant="ghost" size="sm" className="w-full justify-start text-destructive">
                  <LogOut className="h-4 w-4 mr-2" />
                  Log out
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  )
}